import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { GROUP_COLORS } from "@/lib/budget";
import type { PlanTotals } from "@/lib/useBudgetLines";
import { money } from "@/lib/format";

const pctOf = (v: number, base: number) => (base > 0 ? Math.round((v / base) * 100) : 0);

export function UnassignedLine({ t, dark = false }: { t: PlanTotals; dark?: boolean }) {
  const over = t.unassigned < -0.005;
  return (
    <p className={`text-sm font-semibold ${over ? (dark ? "text-[#F3B5A6]" : "text-[#B23A1E]") : dark ? "text-amber-400" : "text-[#1B2A4A]"}`} data-testid="plan-unassigned-text">
      {over ? `Estás repartiendo ${money(-t.unassigned)} más de lo que ganas` : `Queda por asignar: ${money(t.unassigned)}`}
    </p>
  );
}

export function PlanBar({ t }: { t: PlanTotals }) {
  return (
    <div className="tabular sticky top-0 z-10 flex flex-col gap-1 rounded-xl bg-[#1B2A4A] px-4 py-3 text-sm text-white sm:flex-row sm:items-center sm:justify-between" data-testid="plan-live-bar">
      <span>Ingreso <b data-testid="plan-bar-income">{money(t.income)}</b> · Asignado <b>{money(t.assigned)}</b></span>
      <UnassignedLine t={t} dark />
    </div>
  );
}

function Reference({ id, label, value, ok }: { id: string; label: string; value: number; ok: boolean }) {
  const Icon = ok ? CheckCircle2 : AlertTriangle;
  return (
    <li className="flex items-center justify-between gap-3 text-sm" data-testid={`plan-ref-${id}`}>
      <span className="flex items-center gap-2 text-slate-600"><Icon className={`h-4 w-4 ${ok ? "text-[#1B6E4F]" : "text-[#B45309]"}`} />{label}</span>
      <span className={`tabular font-semibold ${ok ? "text-[#1B6E4F]" : "text-amber-700"}`}>Vas en {value}%</span>
    </li>
  );
}

export function PlanSummary({ t }: { t: PlanTotals }) {
  const rows: [string, string, number, string][] = [
    ["savings_investment", "Ahorro e Inversión", t.savings, GROUP_COLORS.savings_investment], ["essentials", "Esenciales", t.essentials, GROUP_COLORS.essentials],
    ["comfort", "Comodidad", t.comfort, GROUP_COLORS.comfort], ["debts", "Deudas", t.debts, GROUP_COLORS.debt],
  ];
  const ess = pctOf(t.essentials, t.income), sav = pctOf(t.savings, t.income), casa = pctOf(t.housing, t.income), deu = pctOf(t.debts, t.income);
  return (
    <div className="rounded-[14px] border border-[#DDE1E8] bg-white p-6" data-testid="plan-summary">
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#1B2A4A]">Tu reparto en vivo</p>
      <p className="mt-3 text-sm text-slate-500">Ingreso mensual total</p>
      <p className="tabular font-display text-3xl font-extrabold text-[#1B2A4A]" data-testid="plan-summary-income">{money(t.income)}</p>
      <ul className="mt-5 space-y-3">
        {rows.map(([g, label, v, color]) => (
          <li key={g} data-testid={`plan-summary-${g}`}>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-slate-600"><i className={`h-2.5 w-2.5 rounded-full ${color}`} />{label}</span>
              <span className="tabular font-semibold text-[#1B2A4A]">{money(v)} <span className="font-normal text-slate-400">· {pctOf(v, t.income)}%</span></span>
            </div>
            <div className="mt-1.5 h-1.5 rounded-full bg-[#F4F5F7]"><div className={`h-full rounded-full ${color} transition-[width] duration-500`} style={{ width: `${Math.min(pctOf(v, t.income), 100)}%` }} /></div>
          </li>
        ))}
      </ul>
      <div className={`mt-5 rounded-lg p-3 ${t.unassigned < -0.005 ? "bg-[#F8ECE8]" : "bg-[#F4F5F7]"}`}><UnassignedLine t={t} /></div>
      <p className="mt-5 text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Referencias, solo como guía</p>
      <ul className="mt-3 space-y-2.5">
        <Reference id="essentials" label="Esenciales hasta 50%" value={ess} ok={ess <= 50} />
        <Reference id="savings" label="Ahorro de 10% a 20%" value={sav} ok={sav >= 10} />
        <Reference id="housing" label="Casa (Vivienda) hasta 30%" value={casa} ok={casa <= 30} />
        <Reference id="debts" label="Deudas hasta 30%" value={deu} ok={deu <= 30} />
      </ul>
    </div>
  );
}
